/** @jsxImportSource @emotion/react */

/**
 * Canvas — 테마색 배경 래퍼. 위에 children(아이콘/텍스트/이미지 등)을 자유롭게 얹음.
 * 대안 네이밍: Surface (Material 추상 면), Sheet (MUI), Paper (MUI, shadow 있는 종이).
 */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';

// 고정 시드로 불규칙한 섬유 결을 한 번만 생성해 렌더링마다 질감이 바뀌지 않게 한다.
const PAPER_TEXTURE = (() => {
  let seed = 7319;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  const fibers = Array.from({ length: 5000 }, () => {
    const x = 24 + random() * 976;
    const y = 24 + random() * 976;
    const angle = random() * Math.PI * 2;
    const length = 2 + random() * 16;
    const dx = Math.cos(angle) * length;
    const dy = Math.sin(angle) * length;
    const bend = (random() - 0.5) * 5;
    return `<path d="M${x.toFixed(2)} ${y.toFixed(2)} q${(dx / 2 - Math.sin(angle) * bend).toFixed(2)} ${(dy / 2 + Math.cos(angle) * bend).toFixed(2)} ${dx.toFixed(2)} ${dy.toFixed(2)}" stroke-opacity="${(0.025 + random() * 0.045).toFixed(3)}" stroke-width="${(0.3 + random() * 0.5).toFixed(2)}"/>`;
  }).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="0 0 1024 1024"><g fill="none" stroke="black" stroke-linecap="round">${fibers}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
})();

interface IProps {
  theme?: ETheme;
  shape?: 'round' | 'square';
  width?: string;
  height?: string;
  padding?: string;
  alignX?: 'left' | 'center' | 'right';
  alignY?: 'top' | 'center' | 'bottom';
  shadow?: boolean;
  border?: boolean;
  handleClick?: () => void;
  children?: React.ReactNode;
}

const createDesign = (
  theme: ETheme,
  shape: 'round' | 'square',
  shadow: boolean,
  border: boolean,
  clickable: boolean,
  width: string,
  height: string,
  padding: string,
  alignX: 'left' | 'center' | 'right',
  alignY: 'top' | 'center' | 'bottom',
) => {
  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.NONE:
        return { backgroundColor: 'transparent', borderColor: 'transparent' };
      case ETheme.PAPER:
        return { backgroundColor: color.PAPER_1, borderColor: color.GRAY_2 };
      case ETheme.BLACK:
        return { backgroundColor: color.BLACK_2, borderColor: color.BLACK_1 };
      case ETheme.BROWN:
        return { backgroundColor: color.BROWN_4, borderColor: color.BROWN_2 };
      case ETheme.GRAY:
        return { backgroundColor: color.GRAY_5, borderColor: color.GRAY_2 };
      case ETheme.YELLOW:
        return { backgroundColor: color.YELLOW_4, borderColor: color.YELLOW_2 };
      case ETheme.GREEN:
        return { backgroundColor: color.GREEN_4, borderColor: color.GREEN_2 };
      case ETheme.RED:
        return { backgroundColor: color.RED_4, borderColor: color.RED_2 };
      case ETheme.BLUE:
        return { backgroundColor: color.BLUE_4, borderColor: color.BLUE_2 };
      case ETheme.CYAN:
        return { backgroundColor: color.CYAN_5, borderColor: color.CYAN_3 };
      default:
        return { backgroundColor: color.WHITE_1, borderColor: color.GRAY_2 };
    }
  };

  // ############### createTexture ###############
  const createTexture = () => {
    if (theme !== ETheme.PAPER) return { backgroundImage: 'none', backgroundSize: 'auto', backgroundPosition: '0 0' };
    return {
      backgroundImage: [
        PAPER_TEXTURE,
        'linear-gradient(17deg, transparent 46%, rgba(0, 0, 0, 0.035) 49%, transparent 52%)',
        'linear-gradient(103deg, transparent 47%, rgba(0, 0, 0, 0.025) 50%, transparent 53%)',
      ].join(', '),
      backgroundSize: '1024px 1024px, 11px 7px, 17px 13px',
      backgroundPosition: '0 0, 0 0, 3px 7px',
    };
  };

  // ############### createEvent ###############
  const createEvent = () => ({
    cursor: clickable ? ('pointer' as const) : ('default' as const),
  });

  // ############### Combine ###############
  const themeDesign = createTheme();
  const event = createEvent();
  const textureDesign = createTexture();

  return {
    width,
    height,
    padding,
    alignItems: alignX === 'left' ? 'flex-start' : alignX === 'right' ? 'flex-end' : 'center',
    justifyContent: alignY === 'top' ? 'flex-start' : alignY === 'bottom' ? 'flex-end' : 'center',
    backgroundColor: themeDesign.backgroundColor,
    backgroundImage: textureDesign.backgroundImage,
    backgroundSize: textureDesign.backgroundSize,
    backgroundPosition: textureDesign.backgroundPosition,
    borderRadius: shape === 'round' ? size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS : '0px',
    border: border ? `${size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH} solid ${themeDesign.borderColor}` : 'none',
    shadow: shadow ? color.SURFACE_SHADOW : 'none',
    event,
  };
};

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
}

const Wrapper = styled.div<IWrapperProps>`
  display: flex;
  width: ${(props) => props.$design.width};
  height: ${(props) => props.$design.height};
  padding: ${(props) => props.$design.padding};
  flex-direction: column;
  align-items: ${(props) => props.$design.alignItems};
  justify-content: ${(props) => props.$design.justifyContent};
  background-color: ${(props) => props.$design.backgroundColor};
  background-image: ${(props) => props.$design.backgroundImage};
  background-size: ${(props) => props.$design.backgroundSize};
  background-position: ${(props) => props.$design.backgroundPosition};
  border: ${(props) => props.$design.border};
  border-radius: ${(props) => props.$design.borderRadius};
  box-shadow: ${(props) => props.$design.shadow};
  cursor: ${(props) => props.$design.event.cursor};
  box-sizing: border-box;
`;

const Canvas: React.FC<IProps> = ({
  theme = ETheme.BROWN,
  shape = 'round',
  width = 'auto',
  height = 'auto',
  padding = size.MARGIN_1,
  alignX = 'left',
  alignY = 'top',
  shadow = false,
  border = false,
  handleClick,
  children,
}) => {
  const design = createDesign(theme, shape, shadow, border, !!handleClick, width, height, padding, alignX, alignY);

  return (
    <Wrapper $design={design} onClick={handleClick}>
      {children}
    </Wrapper>
  );
};

export default Canvas;
